import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Users, Clock } from "lucide-react";

const CHAT_COLORS = {
  HIV: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: "❤️" },
  HSV: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", icon: "💚" },
  HPV: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: "💙" },
};

export default function Stories() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = async () => {
    try {
      const res = await api.get("/public-chats");
      setChats(res.data || []);
    } catch (err) {
      toast.error("Failed to load community chats");
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (date) => {
    if (!date) return "";
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold">Community Chats</h1>
          <p className="text-sm text-[#6B6B70] mt-1">
            Connect with others in your community. 1 token per message.
          </p>
          {user && (
            <p className="text-xs text-[#6B6B70] mt-2">
              🪙 You have {user.tokens || 0} tokens
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-16">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No community chats available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {chats.map((chat) => {
              const colors = CHAT_COLORS[chat.chat_type] || CHAT_COLORS.HIV;
              return (
                <div
                  key={chat.chat_id}
                  onClick={() => navigate(`/chat/${chat.chat_type.toLowerCase()}`)}
                  className="bg-white border border-[#E7E5E0] rounded-2xl p-5 hover:border-[#D4D2CB] hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center text-2xl flex-shrink-0`}>
                      {colors.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg text-[#0F0F10]">{chat.title}</h3>
                      {chat.last_message ? (
                        <p className="text-sm text-[#6B6B70] truncate mt-1">
                          {chat.last_message}
                        </p>
                      ) : (
                        <p className="text-sm text-[#6B6B70] italic mt-1">
                          No messages yet. Be the first!
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-[#6B6B70]">
                        {chat.last_message_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {timeAgo(chat.last_message_time)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {chat.total_messages || 0} messages
                        </span>
                        {chat.active_users > 0 && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Users className="w-3 h-3" />
                            {chat.active_users} online
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="text-[#6B6B70] flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}